/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */

import {
    CategoryHierarchy as CategoryHierarchyData,
    IProductRefinerHierarchy,
    IProductsDimensionsAvailabilities
} from '@msdyn365-commerce/commerce-entities';
import MsDyn365 from '@msdyn365-commerce/core';
import { getCartFromCustomer } from '@msdyn365-commerce/global-state';
import {
    format,
    ProductPrice,
    ProductRefinerValue,
    ProjectionDomain,
    SortColumn,
    TextValueTranslation,
    DiscountLine
} from '@msdyn365-commerce/retail-proxy';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    ILabeledDropdownOnChangeNotification,
    ILabeledDropdownOption,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    LabeledDropdown,
    UncontrolledPagination
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, observable, reaction, transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import {
    buildListPageUrl,
    getCollectionProducts,
    getCollectionRefinersAction,
    getCurrentUrl,
    GetFullProductsByCollectionInput,
    parseQueryParam,
    RefinersByCollectionInput,
    sortOptions
} from './actions';
import { hydrateRefinersFromUrl } from './actions/url-utils';
import {
    ErrorMessage,
    FeatureSearchResultItem,
    getUpdatedRefinementCriteria,
    IChoiceSummaryClickNotification,
    IRefineItemCommonProps,
    IRefineItemToggleNotification,
    ISearchResultModalViewProps,
    isMatchingRefinementCriterion,
    Link,
    ModalToggle,
    ProductSearchResultItems,
    SearchResultModal,
    Separator,
    Title
} from './components';
import ChoiceSummary from './components/choice-summary';
import RefineSubmenu from './components/refine-submenu';
import { IDobbiesSearchResultContainerData } from './dobbies-search-result-container.data';
import { expandRefinersCount, IDobbiesSearchResultContainerProps } from './dobbies-search-result-container.props.autogenerated';
import { ProductRefinerSource } from '@msdyn365-commerce/retail-proxy';
import { UNXProductIdInput } from '../../themes/dobbies/actions/unx-req-id-data.action';
// import { useState, useEffect } from  "react";
export interface ISearchResultContainerViewProps extends IDobbiesSearchResultContainerProps<IDobbiesSearchResultContainerData> {
    products?: React.ReactNode;
    className?: string;
    SearchResultContainer: IModuleProps;
    TitleViewProps: ITitleViewProps;
    categoryHierarchy: ICategoryHierarchyViewProps;
    pagination?: React.ReactNode;
    ProductsContainer: INodeProps;
    ProductSectionContainer: INodeProps;
    refineMenu: IRefineMenuViewProps;
    sortByOptions: ISortByViewProps;
    choiceSummary?: React.ReactNode;
    modalToggle: React.ReactNode;
    searchResultModal: ISearchResultModalViewProps;
    isMobile: boolean;
    CategoryNavContainer: INodeProps;
    RefineAndProductSectionContainer: INodeProps;
    errorMessage: React.ReactNode;
    FeatureSearchContainer: INodeProps;
    similarLookProduct?: React.ReactNode;
    productPrices?: ProductPrice[];
    refinersRef: React.RefObject<HTMLDivElement>;
    onMoreFiltersClicked?(keyName: string | undefined, isExpanded: boolean, isFromMoreFilters?: boolean): void;
}

export interface ITitleViewProps {
    TitleContainer: INodeProps;
    title: ISearchResultTitle;
}

export interface IRefineMenuViewProps {
    RefineMenuContainer: INodeProps;
    RefinerSectionContainer: INodeProps;
    refiners?: React.ReactNode[];
    endingRefiners?: React.ReactNode[];
}

export interface ICategoryHierarchyViewProps {
    categoryHierarchyList?: React.ReactNode[];
    categoryHierarchySeparator?: React.ReactNode;
    CategoryHierarchyContainer: INodeProps;
}

export interface ISortByViewProps {
    SortingContainer: INodeProps;
    sortByDropDown?: React.ReactNode;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Title component for search result container.
 */
export interface ISearchResultTitle {
    titlePrefix?: React.ReactNode;
    titleText?: React.ReactNode;
    titleCount?: React.ReactNode;
}

export interface ISearchResultContainerState {
    sortingState: ISortByCollectionState;
    modalIsOpen: boolean;
    refiners: IProductRefinerHierarchy[];
    discountLines: (DiscountLine[] | undefined)[];
    showProdPrice: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    isUpdated: boolean;
    nonClubPlusDiscount: ProductPrice[];
    updatedProducts: number[] | undefined;
    lastUrlStr: string;
    refinersExpandedStateData?: IDobbiesRefinerExpandedState[][];
    scrollClass: boolean;
    shouldShowPLPAds?: boolean;
}

interface ISortByCollectionState {
    selectedSortByOption: ILabeledDropdownOption;
    pending: boolean;
}

export interface IDobbiesRefinerExpandedState {
    refinerId?: number;
    isExpanded: boolean;
    keyName?: string;
}

/**
 *
 * SearchResultContainer component.
 * @extends {React.PureComponent<IDobbiesSearchResultContainerProps<IDobbiesSearchResultContainerData>>}
 */
@observer
export default class SearchResultContainer extends React.PureComponent<
    IDobbiesSearchResultContainerProps<IDobbiesSearchResultContainerData>,
    ISearchResultContainerState
> {
    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm' || this._viewport === 'md';
    }

    private readonly sortByDropdownOptions: ILabeledDropdownOption[] = [
        { key: sortOptions.sortByOptionRelevanceDesc, value: this.props.resources.sortByOptionRelevanceDesc },
        { key: sortOptions.sortByOptionNameAsc, value: this.props.resources.sortByOptionNameAsc },
        { key: sortOptions.sortByOptionNameDesc, value: this.props.resources.sortByOptionNameDesc },
        { key: sortOptions.sortByOptionPriceAsc, value: this.props.resources.sortByOptionPriceAsc },
        { key: sortOptions.sortByOptionPriceDesc, value: this.props.resources.sortByOptionPriceDesc },
        { key: sortOptions.sortByOptionRatingDesc, value: this.props.resources.sortByOptionRatingDesc }
    ];

    private readonly _refineItemCommonProps: IRefineItemCommonProps;

    private readonly _pageType: string | undefined = this.props.context.request.urlTokens.pageType;

    @observable
    private _viewport: GridSize;

    private readonly _modalToggleRef: React.RefObject<HTMLButtonElement>;

    private readonly _sortAndFilterContainerRef: React.RefObject<LabeledDropdown>;

    private expandrefinerCount: number | undefined = 0;

    private readonly telemetryContent: ITelemetryContent;

    private productPrices: ProductPrice[];

    private readonly defaultPageNumber = 0;

    private refinersRef: React.RefObject<HTMLDivElement>;

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && nameTranslations.length > 0) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation && nameTranslation.Text;
    }

    private lastChangedScrollStateMobile: Date | undefined;

    constructor(props: IDobbiesSearchResultContainerProps<IDobbiesSearchResultContainerData>, state: ISearchResultContainerState) {
        super(props);
        this._viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._modalToggleRef = React.createRef<HTMLButtonElement>();
        this._sortAndFilterContainerRef = React.createRef<LabeledDropdown>();
        this._toggleModal = this._toggleModal.bind(this);
        this._updateViewport = this._updateViewport.bind(this);
        this.productPrices = [];
        this.refinersRef = React.createRef();
        this.state = {
            sortingState: {
                pending: false,
                selectedSortByOption: this.sortByDropdownOptions[0]
            },
            modalIsOpen: false,
            refiners: [],
            discountLines: [],
            showProdPrice: false,
            isUpdated: false,
            nonClubPlusDiscount: [],
            updatedProducts: [],
            lastUrlStr: '',
            scrollClass: false
        };
        const { placeholderTextMax, minLabel, maxLabel, rangeNameFormat } = this.props.resources;

        const locale = this.props.context.request.locale;
        const telemetry = this.props.telemetry;
        const validationErrorNaN = this.props.resources.validationErrorNotNumber;
        const validationErrorRange = this.props.resources.validationErrorNotRange;
        this._refineItemCommonProps = {
            telemetry,
            locale,
            placeholderTextMax,
            minLabel,
            maxLabel,
            rangeNameFormat,
            validationErrorNaN,
            validationErrorRange
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        // VSI-Customization -- START check & redirect if found url in returns of search query
        if (props.data.products.result && props.data.products.result.count === 0) {
            const redirectUrl = props.data.products.result.products[0].ExtensionProperties?.find(item => item.Key === 'RedirectURL');
            if (redirectUrl && redirectUrl.Value!.StringValue && MsDyn365.isBrowser && window) {
                window.location.assign(redirectUrl.Value!.StringValue);
            }
        }
        /* VSI-Customization -- END  */

        // Initalization of list page
        this.props.data.products.then(products => {
            this.props.data.listPageState.then(listPageState => {
                if (this._pageType === 'Category') {
                    listPageState.pageType = 'Category';
                } else {
                    listPageState.pageType = 'Search';
                }

                let querySorting: SortColumn[] = [];

                if (this.props.context.request.query && this.props.context.request.query.sorting) {
                    querySorting = JSON.parse(decodeURIComponent(this.props.context.request.query.sorting)) as SortColumn[];
                }

                listPageState.currentPageNumber =
                    (this.props.context.request.query && +this.props.context.request.query.skip / (this.props.config.itemsPerPage || 10)) ||
                    0;
                listPageState.sortingCritera = { Columns: querySorting };
                listPageState.pageSize = this.props.config.itemsPerPage || 10;
                listPageState.activeProducts = products.products;
                listPageState.totalProductCount = products.count;
                listPageState.activeFilters = hydrateRefinersFromUrl(this.props.context.request);

                // Initialize reaction based on listPageState properties
                reaction(
                    () => {
                        return [
                            listPageState.activeFilters && listPageState.activeFilters.length,
                            listPageState.currentPageNumber,
                            listPageState.sortingCritera && this.state.isUpdated
                        ];
                    },
                    () => {
                        const input = new GetFullProductsByCollectionInput(
                            listPageState.pageType,
                            this.props.context.request.apiSettings,
                            {
                                Paging: {
                                    Top: this.props.config.itemsPerPage,
                                    Skip: Math.max(listPageState.pageSize * (listPageState.currentPageNumber || 0), 0)
                                },
                                count: true,
                                Sorting: listPageState.sortingCritera || {}
                            },
                            listPageState.activeFilters || [],
                            +(this.props.context.request.urlTokens.itemId || 0),

                            this.props.context.request.query && this.props.context.request.query.q
                                ? this.props.context.request.query.q
                                : JSON.stringify({
                                      ProductId: this.props.context.request.query?.productId,
                                      Recommendation: this.props.context.request.query?.recommendation
                                  }),
                            !!(
                                this.props &&
                                this.props.config &&
                                this.props.config.includeAttributes !== undefined &&
                                this.props.config.includeAttributes
                            ),
                            this.props.config.updateRefinerPanel,
                            this.props.context.request.locale
                        );
                        getCollectionProducts(input, this.props.context.actionContext).then(productResults => {
                            listPageState.activeProducts = productResults.products;
                            if (productResults.products.length && productResults.count) {
                                listPageState.totalProductCount = productResults.count;
                            } else if (!productResults.products.length && !productResults.count) {
                                listPageState.totalProductCount = productResults.count;
                            }
                            // if (
                            //     this.props.config.enableAffiliationBasedPricing &&
                            //     this.props.context.actionContext.requestContext?.user?.isAuthenticated
                            // ) {
                            //     this._getActivePrice();
                            // }
                            if (this.props.config.enableAffiliationBasedPricing) {
                                this._getActivePrice();
                            }
                        });

                        if (this.props.config.updateRefinerPanel) {
                            const refinerInput = new RefinersByCollectionInput(
                                listPageState.pageType,
                                this.props.context.request.apiSettings,
                                {
                                    Paging: {
                                        Top: this.props.config.itemsPerPage,
                                        Skip: Math.max(
                                            listPageState.pageSize * (listPageState.currentPageNumber ?? this.defaultPageNumber),
                                            this.defaultPageNumber
                                        )
                                    },
                                    count: true,
                                    Sorting: listPageState.sortingCritera ?? {}
                                },
                                listPageState.activeFilters ?? [],
                                +(this.props.context.request.urlTokens.itemId ?? 0),
                                this.props.context.request.query?.q
                                    ? this.props.context.request.query.q
                                    : this.props.context.request.query?.productId,
                                false,
                                this.props.config.updateRefinerPanel
                            );

                            getCollectionRefinersAction(refinerInput, this.props.context.actionContext)
                                .then(refiners => {
                                    this.setState({ refiners });
                                })
                                .catch(error => {
                                    this.props.telemetry.error(error);
                                });
                        }
                    }
                );
            });
        });

        if (this.props.context.request.query?.productId) {
            this.props.data.featureProduct.then(products => {
                this.props.data.listPageState.then(listPageStateFeature => {
                    listPageStateFeature.featureProduct = products;
                });
            });
        }
        this._updateViewport();
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser && window && window && window.addEventListener) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
            /** Add scroll listener */
            window.addEventListener('scroll', this._mainMenuScroll);
        }
        if (this.props.config.enableAffiliationBasedPricing) {
            this._getActivePrice();
        }
        // if (this.props.config.enableAffiliationBasedPricing && this.props.context.actionContext.requestContext?.user?.isAuthenticated) {
        //     this._getActivePrice();
        // }
        this.getNonClubPlusDiscountPrices();
        reaction(
            () => this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts,
            async () => {
                const url = MsDyn365.isBrowser && window && window.location.href;
                if (this.props.data.listPageState.result?.activeProducts.length && this.state.lastUrlStr !== url) {
                    this.getNonClubPlusDiscountPrices();
                    this.setState({ lastUrlStr: url ? url : '' });
                }
            }
        );
        if (MsDyn365.isBrowser) {
            setTimeout(() => {
                this.setState({ shouldShowPLPAds: true });
            }, 800);
        }

        if (MsDyn365.isBrowser && window && window.addEventListener) {
            window.addEventListener('scroll', this._mainMenuScroll);
        }

        const prods = this.props.data.products.result?.products;
        if (prods?.length) {
            const unxReqId = prods[0].ExtensionProperties?.find(ep => ep.Key === 'Unx-Request-Id')?.Value?.StringValue;
            this.props.context.actionContext.update(new UNXProductIdInput(), { unxReqId: unxReqId });
        }
    }

    public checkDifference = (array1: number[] | undefined, array2: number[] | undefined) => {
        if (!array1 || !array2) {
            return true;
        }
        if (array1?.length !== array2?.length) {
            return true;
        }
        for (let i = 0; i < array1.length; i++) {
            if (array1[i] !== array2[i]) {
                return true;
            }
        }
        return false;
    };

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser && window.removeEventListener) {
            window.removeEventListener('resize', this._updateViewport);
            /** Remove scroll listener */
            window.removeEventListener('scroll', this._mainMenuScroll);
        }
    }

    public render(): JSX.Element {
        const { imageSettings, className } = this.props.config;
        const { resources } = this.props;
        // VSI-Customization START - check empty product object on searching special character.
        const products =
            (this.props.data.listPageState.result &&
            this.props.data.listPageState.result.activeProducts &&
            this.props.data.listPageState.result.activeProducts.length === 1 &&
            !this.props.data.listPageState.result.activeProducts[0].ItemId
                ? []
                : this.props.data.listPageState.result!.activeProducts) || [];
        // VSI-Customization END
        const searchProduct = (this.props.data.listPageState.result && this.props.data.listPageState.result.featureProduct) || null;
        let errorText = '';
        if (!products || products.length === 0) {
            errorText = this._pageType === 'Category' ? resources.resultCategoryNotFoundText : resources.resultSearchNotFoundText;
        }
        const productsComponent = this._getProducts();
        const featureComponent = (
            <FeatureSearchResultItem
                product={searchProduct}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                telemetryContent={this.telemetryContent}
            />
        );

        const searchResultContainerViewProps = {
            ...this.props,

            products: productsComponent,
            TitleViewProps: !this.props.config.disableHierarchy && this._getCollectionTitle(),
            categoryHierarchy: !this.props.config.disableHierarchy && this._getCategoryHierarchy(),
            refineMenu: this._getRefineMenu(),
            className: classnames('ms-search-result-container', className),
            SearchResultContainer: {
                moduleProps: this.props,
                className: classnames('ms-search-result-container new-filter-ms-search-result-container', className)
            },
            sortByOptions:
                this.props.data.listPageState.result && this.props.data.listPageState.result.totalProductCount !== 0
                    ? this._getSortingDropDown()
                    : null,
            pagination: this._getPagination(),
            ProductsContainer: { className: 'ms-search-result-container__Products' },
            ProductSectionContainer: { className: 'ms-search-result-container__product-section' },
            CategoryNavContainer: { className: 'ms-search-result-container__category-nav-section new-filter__category-nav-section' },
            RefineAndProductSectionContainer: {
                className: 'ms-search-result-container__refine-product-section new-filter__refine-product-section'
            },
            choiceSummary: this._getChoiceSummary(),
            modalToggle:
                this.props.data.listPageState.result && this.props.data.listPageState.result.totalProductCount !== 0 ? (
                    <ModalToggle
                        text={resources.modalTitle}
                        ariaLabel={resources.modalTitle}
                        innerRef={this._modalToggleRef}
                        onClick={this._toggleModal}
                        id='search-result-modal'
                    />
                ) : null,
            searchResultModal: this._getSearchResultModal(),
            isMobile: this.isMobile,
            errorMessage: errorText && <ErrorMessage text={errorText} />,
            FeatureSearchContainer: { className: 'ms-feature-search-result' },
            similarLookProduct:
                this.props.data.listPageState.result &&
                this.props.data.listPageState.result.featureProduct !== null &&
                this.props.data.listPageState.result.featureProduct !== undefined
                    ? featureComponent
                    : null,
            productPrices: this.productPrices,
            refinersRef: this.refinersRef,
            onMoreFiltersClicked: this.toggleRefinersInSameColumn
        };
        return this.props.renderView(searchResultContainerViewProps) as React.ReactElement;
    }

    private readonly _getActivePrice = async (): Promise<void> => {
        const priceAffiliations = [
            {
                AffiliationId: this.props.context.app.config.AffiliationId,
                LoyaltyTierId: this.props.context.app.config.LoyaltyTierId,
                AffiliationTypeValue: this.props.context.app.config.AffiliationTypeValue,
                ReasonCodeLines: [],
                CustomerId: '',
                ExtensionProperties: []
            }
        ];

        let products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const productRecIds = ArrayExtensions.hasElements(products) ? products.map(prod => prod.RecordId) : [];
        const projectDomain: ProjectionDomain = {
            ChannelId: this.props.context.request.apiSettings.channelId,
            CatalogId: this.props.context.request.apiSettings.catalogId
        };
        const cart = await getCartFromCustomer(this.props.context.actionContext);
        const affliations = cart?.AffiliationLines;
        console.log(affliations);
        await getActivePricesAsync(
            {
                callerContext: this.props.context.actionContext,
                queryResultSettings: {}
            },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            priceAffiliations,
            true
        )
            .then((activePricesResponse: ProductPrice[]) => {
                this.productPrices = activePricesResponse;
                const discountLines = activePricesResponse.map(priceResp => priceResp.DiscountLines);
                const prodIds: number[] = [];
                activePricesResponse.map(priceResp => {
                    if (priceResp.ProductId) {
                        prodIds.push(priceResp.ProductId);
                    }
                });
                if (this.checkDifference(prodIds, this.state.updatedProducts)) {
                    this.setState({
                        discountLines: discountLines,
                        showProdPrice: true,
                        updatedProducts: prodIds
                    });
                }

                products = products.map(prod => {
                    const activePrice = activePricesResponse.find(price => price.ProductId === prod.RecordId);
                    if (activePrice) {
                        prod.Price = activePrice.CustomerContextualPrice ?? prod.Price;
                        prod.BasePrice = Math.max(
                            activePrice.BasePrice || 0,
                            activePrice.TradeAgreementPrice || 0,
                            activePrice.AdjustedPrice || 0
                        );
                    }
                    return prod;
                });
                products = products?.filter(prod => {
                    const activePrice = activePricesResponse.find(price => price.ProductId === prod.RecordId);
                    if (activePrice) {
                        return true;
                    }
                    return false;
                });
                this.props.data.listPageState.then(listPageState => {
                    listPageState.activeProducts = products;
                });
            })
            .catch((error: Error) => {
                this.setState({
                    showProdPrice: true
                });
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
                this.props.context.actionContext.telemetry.debug(
                    '[get Affiliation based price on search result container ]Error executing action'
                );
                throw new Error('[get Affiliation based price on search result container]Error executing action');
            });
    };

    private readonly getNonClubPlusDiscountPrices = async (): Promise<void> => {
        const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const productRecIds = ArrayExtensions.hasElements(products) ? products.map(prod => prod.RecordId) : [];
        const projectDomain: ProjectionDomain = {
            ChannelId: this.props.context.request.apiSettings.channelId,
            CatalogId: this.props.context.request.apiSettings.catalogId
        };
        await getActivePricesAsync(
            {
                callerContext: this.props.context.actionContext,
                queryResultSettings: {}
            },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            null,
            true
        )
            .then((activePricesResponse: ProductPrice[]) => {
                if (activePricesResponse && activePricesResponse.length > 0) {
                    activePricesResponse = activePricesResponse.filter(prod => {
                        const activePrice = products?.find(price => price.RecordId === prod.ProductId);
                        if (activePrice) {
                            return true;
                        }
                        return false;
                    });
                    this.setState({
                        nonClubPlusDiscount: activePricesResponse
                    });
                }
            })
            .catch((error: Error) => {
                this.setState({
                    showProdPrice: true
                });
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
                this.props.context.actionContext.telemetry.debug(
                    '[get Affiliation based price on search result container ]Error executing action'
                );
                throw new Error('[get Affiliation based price on search result container]Error executing action');
            });
    };

    private readonly _getSearchResultModal = (): ISearchResultModalViewProps => {
        const { resources } = this.props;
        return SearchResultModal({
            resources: {
                modalCloseButtonText: resources.modalCloseButtonText,
                modalTitle: resources.modalTitle
            },
            isOpen: this.state.modalIsOpen,
            returnRef: this._modalToggleRef,
            onModalToggle: this._toggleModal
        });
    };

    private readonly _getCollectionTitle = (): ITitleViewProps => {
        const { data, context, resources } = this.props;

        let collectionTitle: string | undefined = '';
        if (context && context.request && context.request.query && context.request.query.q) {
            collectionTitle = `"${context.request.query.q}"`;
        } else {
            collectionTitle =
                (data.category.result &&
                    SearchResultContainer.getFriendlyName(context.request.locale, data.category.result.NameTranslations)) ||
                (data.category.result && data.category.result.Name);
        }
        let productCountText = '';
        let productCountNumber: number | undefined;
        if (data.listPageState && data.listPageState.result && data.listPageState.result.totalProductCount !== undefined) {
            productCountNumber = data.listPageState.result.totalProductCount;
        } else if (data.products && data.products.result) {
            productCountNumber = data.products.result.count;
        }

        if (productCountNumber && productCountNumber !== 0) {
            productCountText =
                productCountNumber !== 1
                    ? format(this.props.resources.numberOfProducts, productCountNumber)
                    : this.props.resources.oneProduct;
        } else {
            productCountText = format(this.props.resources.numberOfProducts, 0);
        }
        const isSearchPage = this.props.context.request.query?.q;
        const pageName = context.request.telemetryPageName ? `result-count-${context.request.telemetryPageName.toLowerCase()}` : '';
        const titlePrefix = <Title className='ms-search-result__collection-title-prefix' text={resources.searchTextPrefix} />;
        const titleText = collectionTitle && <Title className='ms-search-result__collection-title-text' text={collectionTitle} />;
        const titleCount = <Title className='ms-search-result__collection-title-count' text={productCountText} />;

        return {
            TitleContainer: { className: `ms-search-result-container__title ${collectionTitle ? '' : 'd-none'} ${pageName}` },
            title: {
                titlePrefix,
                titleText,
                titleCount: isSearchPage ? titleCount : undefined
            }
        };
    };

    private readonly _getCategoryHierarchy = (): ICategoryHierarchyViewProps => {
        const { data } = this.props;
        const categoryHierarchy = data.categoryHierarchy.result;
        sessionStorage.setItem('categoryHierarchy', JSON.stringify(categoryHierarchy));
        const categoryLinks =
            categoryHierarchy &&
            categoryHierarchy.map((value: CategoryHierarchyData, index: number) => {
                return (
                    <Link
                        key={index}
                        text={value.Name}
                        ariaLabel={`${this.props.resources.categoryLinkAriaLabel} ${value.Name}`}
                        href={value.Url}
                    />
                );
            });
        const categoryLinkSeparator = <Separator separator='/' />;

        return {
            CategoryHierarchyContainer: { tag: 'nav', className: 'ms-search-result-container__category-hierarchy' },
            categoryHierarchyList: categoryLinks,
            categoryHierarchySeparator: categoryLinkSeparator
        };
    };

    private readonly _getSortingDropDown = (): ISortByViewProps => {
        const { resources } = this.props;
        const activeDropdown = this._getCurrentlySelectedOption() || this.state.sortingState.selectedSortByOption;
        let dropdwonOptions = this.sortByDropdownOptions;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- App config settings are of "any" value.
        if (this.props.context.app.config.hideRating) {
            dropdwonOptions = this.sortByDropdownOptions.filter(dropdownOption => dropdownOption.key !== 'AverageRating-Desc');
        }
        const dropdown = (
            <LabeledDropdown
                labelClassname='reviews-list-sort-by'
                labelText={resources.sortByDropdownLabel}
                dropdownId='categorySortByDropdown'
                dropdownClassname='reviews-list-dropdown'
                toggleColor='link'
                dropdownOptions={dropdwonOptions}
                selectedOption={activeDropdown}
                onSelectOption={this._updateSortByDropdown}
                ref={this._sortAndFilterContainerRef}
            />
        );
        return {
            SortingContainer: { className: 'ms-search-result-container__Sort-by-category' },
            sortByDropDown: dropdown
        };
    };

    private readonly _getPagination = (): React.ReactNode => {
        const { config, context, data, resources } = this.props;
        const listPageState = data && data.listPageState && data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 10;
        const skipCount =
            listPageState && listPageState.currentPageNumber !== null
                ? listPageState.currentPageNumber * (this.props.config.itemsPerPage || 10)
                : (this.props.context.request.query && +this.props.context.request.query.skip) || 0;
        const totalItems = (listPageState && listPageState.totalProductCount) || 0;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        if (totalItems <= itemsPerPage) {
            return null;
        }
        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                telemetryContent={this.telemetryContent}
            />
        );
    };

    private toggleRefinersInSameColumn = (keyName: string, isExpanded: boolean, isFromMoreFilters?: boolean) => {
        if (this.state.refinersExpandedStateData && this.state.refinersExpandedStateData.length) {
            const expandedStateData: IDobbiesRefinerExpandedState[][] = [];
            this.state.refinersExpandedStateData.map(refRow => {
                expandedStateData.push(refRow);
            });
            if (isFromMoreFilters && expandedStateData && expandedStateData.length) {
                let hasFilterOpened = false;
                for (let i = 0; i < expandedStateData.length; i++) {
                    for (let j = 0; j < expandedStateData[i].length; j++) {
                        if (!expandedStateData[i][j].isExpanded) {
                            hasFilterOpened = true;
                        }
                    }
                }
                if (!hasFilterOpened) {
                    return;
                }
                for (let i = 0; i < expandedStateData.length; i++) {
                    for (let j = 0; j < expandedStateData[i].length; j++) {
                        expandedStateData[i][j].isExpanded = true;
                    }
                }
                this.setState({ refinersExpandedStateData: expandedStateData });
            } else if (expandedStateData && expandedStateData.length) {
                for (let i = 0; i < expandedStateData.length; i++) {
                    for (let j = 0; j < expandedStateData[i].length; j++) {
                        if (expandedStateData[i][j] && expandedStateData[i][j].keyName === keyName) {
                            expandedStateData[i][j].isExpanded = isExpanded;
                        }
                        if (expandedStateData[i][j].keyName === keyName && !isExpanded) {
                            for (let k = 0; k < expandedStateData.length; k++) {
                                for (let m = 0; m < expandedStateData[k].length; m++) {
                                    if (expandedStateData[k][m] && expandedStateData[k][m].keyName !== keyName) {
                                        expandedStateData[k][m].isExpanded = true;
                                    }
                                }
                            }
                        }
                    }
                }
                this.setState({ refinersExpandedStateData: expandedStateData });
            }
        }
    };

    private setInitialSameColumnRefinerDetails = (endingRefiners: IProductRefinerHierarchy[] | undefined) => {
        const maxRefinersColumns = this._getViewport() !== 'xl' && this._getViewport() !== 'lg' ? 4 : 5;
        const filtersNavData: (string | undefined)[][] = [];
        let filtersRow: (string | undefined)[] = [];
        if (endingRefiners) {
            for (let index = 0; index < endingRefiners.length; index++) {
                if (index % maxRefinersColumns === 0 && index !== 0) {
                    filtersNavData.push(filtersRow);
                    filtersRow = [];
                }
                filtersRow.push(endingRefiners[index].KeyName);
            }
            if (filtersRow.length) {
                filtersNavData.push(filtersRow);
            }
            const refinersExpandedStateData: IDobbiesRefinerExpandedState[][] = [];
            if (filtersNavData && filtersNavData.length) {
                for (let i = 0; i < filtersNavData.length; i++) {
                    const refinersRowExpandedStates: IDobbiesRefinerExpandedState[] = [];
                    for (let j = 0; j < filtersNavData[i].length; j++) {
                        const filtersKeyName = filtersNavData[i][j];
                        refinersRowExpandedStates.push({ isExpanded: true, keyName: filtersKeyName });
                    }
                    if (refinersRowExpandedStates.length) {
                        refinersExpandedStateData.push(refinersRowExpandedStates);
                    }
                }
            }
            this.setState({ refinersExpandedStateData: refinersExpandedStateData });
        }
    };

    private readonly _getRefineMenu = (): IRefineMenuViewProps => {
        const { data, context, config } = this.props;
        const tempRangeTypeTODO = context.request.query && context.request.query.inputRange ? 'input' : 'slider';
        const validRefiners =
            this.props.config.updateRefinerPanel && ArrayExtensions.hasElements(this.state.refiners)
                ? this.state.refiners.filter(refiner => {
                      return ArrayExtensions.hasElements(refiner.Values);
                  })
                : data.refiners.result &&
                  data.refiners.result.filter(refiner => {
                      return ArrayExtensions.hasElements(refiner.Values);
                  });

        const activeRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        this._getexpandRefinerCount(validRefiners && validRefiners.length, config.expandRefinersCount);

        const sortedRefiners = validRefiners;

        const startingRefinerList = this.props.config.startingRefinerList || [];

        const startingRefiners = sortedRefiners?.filter(refiner => startingRefinerList.indexOf(refiner.KeyName!) > -1);

        let endingRefiners = sortedRefiners?.filter(refiner => {
            const existsInStarting = startingRefiners?.find(startingRefiner => startingRefiner.RecordId === refiner.RecordId);

            return !existsInStarting;
        });

        if (this.props.context.app.config.hideRating) {
            endingRefiners = endingRefiners?.filter(er => er.SourceValue !== ProductRefinerSource.Rating);
        }

        const expandedRefiners = this.expandrefinerCount;

        const subMenus =
            startingRefiners &&
            startingRefiners.map((productRefinerHierarchy: IProductRefinerHierarchy, index: number) => {
                return (
                    <RefineSubmenu
                        productRefinerHierarchy={productRefinerHierarchy}
                        selectedRefinerValues={activeRefiners}
                        refineItemCommonProps={this._refineItemCommonProps}
                        minValueSliderThumbAriaLabel={this.props.resources.minValueSliderThumbAriaLabel}
                        maxValueSliderThumbAriaLabel={this.props.resources.maxValueSliderThumbAriaLabel}
                        key={index}
                        onUpdateRefiners={this._onUpdateRefiners}
                        urlBuilder={this._buildRefinerUrl}
                        isDisabled={false}
                        isExpandedOnInitialLoad={!!(expandedRefiners && expandedRefiners > index)}
                        tempRangeTypeTODO={tempRangeTypeTODO}
                        context={context}
                        moduleId={this.props.id}
                        moduleTypeName={this.props.typeName}
                    />
                );
            });

        const endingSubMenus =
            endingRefiners &&
            endingRefiners.map((productRefinerHierarchy: IProductRefinerHierarchy, index: number) => {
                return (
                    <RefineSubmenu
                        productRefinerHierarchy={productRefinerHierarchy}
                        selectedRefinerValues={activeRefiners}
                        refineItemCommonProps={this._refineItemCommonProps}
                        minValueSliderThumbAriaLabel={this.props.resources.minValueSliderThumbAriaLabel}
                        maxValueSliderThumbAriaLabel={this.props.resources.maxValueSliderThumbAriaLabel}
                        key={index}
                        onUpdateRefiners={this._onUpdateRefiners}
                        urlBuilder={this._buildRefinerUrl}
                        isDisabled={false}
                        isExpandedOnInitialLoad={!!(expandedRefiners && expandedRefiners > index)}
                        tempRangeTypeTODO={tempRangeTypeTODO}
                        context={context}
                        moduleId={this.props.id}
                        moduleTypeName={this.props.typeName}
                        refinersExpandedStateData={this.state.refinersExpandedStateData}
                        onToggle={this.toggleRefinersInSameColumn}
                    />
                );
            });
        if (!this.state.refinersExpandedStateData) {
            this.setInitialSameColumnRefinerDetails(endingRefiners);
        }
        return {
            RefineMenuContainer: { className: `ms-search-result-container__refine-menu` },
            RefinerSectionContainer: {
                className: `ms-search-result-container__refiner-section new-filter__refiner-section ${
                    this.state.scrollClass ? 'scrolled' : ''
                }`
            },
            refiners: subMenus,
            endingRefiners: endingSubMenus
        };
    };

    private readonly _getChoiceSummary = (): React.ReactNode => {
        const { resources, data, telemetry } = this.props;
        const selectedRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
        return (
            <ChoiceSummary
                classNames='ms-choice-summary-by-category'
                clearAllText={resources.clearAllText}
                FilterActiveText={resources.filterActiveText}
                label={resources.choiceSummaryLabel}
                selectedChoices={selectedRefiners}
                // @ts-expect-error: NOTE Type-unsafe line below, null refinersByCategoryHierarchy case not handled
                refinerHierarchy={data.refiners.result}
                choiceFormat={resources.choiceFormat}
                choiceRangeValueFormat={resources.choiceRangeValueFormat}
                telemetry={telemetry}
                onChoiceClicked={this._onChoiceClicked}
                urlBuilder={this._buildRefinerUrlForChoiceSummary}
                choiceAriaLabel={resources.choiceAriaLabel}
                telemetryContent={this.telemetryContent}
                context={this.props.context}
            />
        );
    };

    private readonly _getProducts = (): JSX.Element => {
        const { imageSettings, allowBackNavigation } = this.props.config;
        const { resources } = this.props;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;
        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const quickviewslot = this.props.slots && this.props.slots.quickview && this.props.slots.quickview[0];
        const isPriceMinMaxFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );

        if (this.state.shouldShowPLPAds) {
            return (
                <ProductSearchResultItems
                    key={'plp-ads-search-result-items'}
                    products={products}
                    context={this.props.context}
                    imageSettings={imageSettings}
                    resources={resources}
                    moduleType={this.props.typeName}
                    moduleId={this.props.id}
                    allowBackNavigation={allowBack}
                    telemetryContent={this.telemetryContent}
                    quickviewSlot={quickviewslot}
                    channelInventoryConfigurationId={this.props.data.products.result?.channelInventoryConfigurationId}
                    isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                    discountLines={this.state.discountLines}
                    config={this.props.config}
                    showProdPrice={this.state.showProdPrice}
                    wishlists={this.props.data.wishlists.result}
                    isMobile={this.isMobile}
                    loyalty={this.props.data.loyaltyCard.result}
                    nonClubPlusDiscount={this.state.nonClubPlusDiscount && this.state.nonClubPlusDiscount}
                    showTilesOnPLP={this.props.config.showTilesOnPLP}
                    tilesForPLP={this.props.config.tilesForPLP}
                    repeatSameTile={this.props.config.repeatSameTile}
                    maxNumberOfTiles={this.props.config.maxNumberOfTiles}
                    tileToAppearAfterProducts={this.props.config.tileToAppearAfterProducts}
                    plpAdsSchedulingData={this.props.data.plpAdsSchedulingData.result}
                />
            );
        }

        return (
            <ProductSearchResultItems
                key={'normal-items'}
                products={products}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                allowBackNavigation={allowBack}
                telemetryContent={this.telemetryContent}
                quickviewSlot={quickviewslot}
                channelInventoryConfigurationId={this.props.data.products.result?.channelInventoryConfigurationId}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                discountLines={this.state.discountLines}
                config={this.props.config}
                showProdPrice={this.state.showProdPrice}
                wishlists={this.props.data.wishlists.result}
                isMobile={this.isMobile}
                loyalty={this.props.data.loyaltyCard.result}
                nonClubPlusDiscount={this.state.nonClubPlusDiscount && this.state.nonClubPlusDiscount}
            />
        );
    };

    private readonly _updateSortByDropdown = (notification: ILabeledDropdownOnChangeNotification): void => {
        const { context } = this.props;
        const requestContext = context && context.request;
        const actionContext = context && context.actionContext;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `[sort-by-category] cannot sort without context: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
        }

        MsDyn365.isBrowser &&
            window &&
            window.history.pushState(
                {},
                '',
                buildListPageUrl(
                    getCurrentUrl(this.props.context.request),
                    undefined,
                    [this._getSortColumnFromSelectedOption(notification.selectedOption)],
                    undefined
                )
            );
        this.setState({ isUpdated: !this.state.isUpdated, discountLines: [] });

        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.sortingCritera = {
                Columns: [this._getSortColumnFromSelectedOption(notification.selectedOption)]
            };
        });
    };

    private readonly _getSortColumnFromSelectedOption = (option: ILabeledDropdownOption): SortColumn => {
        const { data } = this.props;

        if (!data.searchConfiguration.result) {
            return {};
        }

        const mappedConfiguration = data.searchConfiguration.result.find(searchConfiguration => {
            return searchConfiguration.key === option.key;
        });

        if (mappedConfiguration) {
            return mappedConfiguration.sortColumn;
        }

        return {};
    };

    private readonly _getCurrentlySelectedOption = (): ILabeledDropdownOption | undefined => {
        const { data } = this.props;
        const sortCriteria = data.listPageState.result && data.listPageState.result.sortingCritera;

        const activeSortColumn =
            (sortCriteria && sortCriteria.Columns && sortCriteria.Columns[0]) ||
            (parseQueryParam<SortColumn[]>('sorting', this.props.context.request) || [])[0];

        if (activeSortColumn && data.searchConfiguration.result) {
            const activeMappedConfig = data.searchConfiguration.result.find(mappedSearchConfig => {
                return (
                    mappedSearchConfig.sortColumn.ColumnName === activeSortColumn.ColumnName &&
                    mappedSearchConfig.sortColumn.IsDescending === activeSortColumn.IsDescending
                );
            });
            if (activeMappedConfig) {
                return this.sortByDropdownOptions.find(dropdownOption => dropdownOption.key === activeMappedConfig.key);
            }
        }

        return;
    };

    private readonly _onUpdateRefiners = (itemToggleNotification: IRefineItemToggleNotification): void => {
        const { context } = this.props;
        const requestContext = context && context.request;
        const actionContext = context && context.actionContext;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `Refine menu cannot refine search criteria: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
        }

        const updatedRefinementCriteria = getUpdatedRefinementCriteria(
            itemToggleNotification,
            (this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters) || []
        );

        MsDyn365.isBrowser &&
            window &&
            window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), updatedRefinementCriteria));
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.activeFilters = updatedRefinementCriteria;
        });
    };

    private readonly _buildRefinerUrl = (itemToggleNotification: IRefineItemToggleNotification): string => {
        if (this.props.data.listPageState.result) {
            const newRefinementCriteria = getUpdatedRefinementCriteria(
                itemToggleNotification,
                this.props.data.listPageState.result.activeFilters || []
            );

            return buildListPageUrl(getCurrentUrl(this.props.context.request), newRefinementCriteria);
        }
        this.props.telemetry.warning('[buildRefinerQueryString]List Page State Not available, unable to build refiner URL');
        return '';
    };

    private readonly _onChoiceClicked = (notification: IChoiceSummaryClickNotification): void => {
        const { data, telemetry } = this.props;

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._onChoiceClicked]ListPageState unavailable, unable to update refiners.');
            return;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (!notification.clearAll) {
            const selectedChoice = notification.choiceClicked;
            if (!selectedChoice) {
                telemetry.warning('[choice-summary-by-category._onChoiceClicked] Choice could not be determined');
                return;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedChoice, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];

            // Set focus to sort and filter
            const dropdownElementId = this._sortAndFilterContainerRef.current && this._sortAndFilterContainerRef.current.props.dropdownId;
            const dropdownElement = dropdownElementId && document.getElementById(dropdownElementId);
            setTimeout(() => {
                dropdownElement && dropdownElement.focus();
            }, 50);
        }

        const fullUrl = getCurrentUrl(this.props.context.request);
        MsDyn365.isBrowser && window && window.history.pushState({}, '', buildListPageUrl(fullUrl, currentRefinementCriteria));
        data.listPageState.result.currentPageNumber = 0;
        data.listPageState.result.activeFilters = currentRefinementCriteria;
    };

    private readonly _buildRefinerUrlForChoiceSummary = (selectedRefiner: ProductRefinerValue, isClearAll: boolean): string => {
        const { data, telemetry } = this.props;
        const fullUrl = getCurrentUrl(this.props.context.request);

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._buildRefinerUrl]ListPageState unavailable, unable to create refiner URL.');
            return fullUrl.href;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (!isClearAll) {
            if (!selectedRefiner) {
                telemetry.warning('[choice-summary-by-category._buildRefinerUrl] URL for Choice could not be determined');
                return fullUrl.href;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedRefiner, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];
        }

        return buildListPageUrl(fullUrl, currentRefinementCriteria);
    };

    private _getViewport(): GridSize {
        const { context } = this.props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'lg';
    }

    private _toggleModal(): void {
        this.setState({
            modalIsOpen: !this.state.modalIsOpen
        });
    }

    private _updateViewport(): void {
        this._viewport = this._getViewport();

        if (this.state.modalIsOpen && !this.isMobile) {
            this._toggleModal();
        }
    }

    private readonly _getexpandRefinerCount = (validRefiners?: number, refinerCount?: expandRefinersCount): void => {
        switch (refinerCount) {
            case expandRefinersCount.all:
                this.expandrefinerCount = validRefiners;
                break;
            case expandRefinersCount.one:
                this.expandrefinerCount = 1;
                break;
            case expandRefinersCount.two:
                this.expandrefinerCount = 2;
                break;
            case expandRefinersCount.three:
                this.expandrefinerCount = 3;
                break;
            case expandRefinersCount.four:
                this.expandrefinerCount = 4;
                break;
            default:
                this.expandrefinerCount = 0;
        }
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    private isInViewport = () => {
        const bounding = this.refinersRef.current?.getBoundingClientRect();
        const refinerMenu =
            MsDyn365.isBrowser && window && (document.querySelector('.ms-search-result-container__refine-menu') as HTMLElement);
        const refinerMenuHeight = refinerMenu ? refinerMenu.offsetHeight : 0;
        const categoryBanners = MsDyn365.isBrowser && window && document.getElementsByClassName('category-banner');

        const HamburgerMenuBtn =
            MsDyn365.isBrowser && window && (document.getElementsByClassName('ms-header__nav-icon msc-btn')[0] as HTMLElement);
        // const contentBlock =
        //     MsDyn365.isBrowser &&
        //     window &&
        //     document.getElementsByClassName('ms-content-block category-banner').length &&
        //     (document.getElementsByClassName('ms-content-block category-banner')[
        //         document.getElementsByClassName('ms-content-block category-banner').length - 1
        //     ] as HTMLElement);
        const isHamburgerMenuBtn = HamburgerMenuBtn ? HamburgerMenuBtn.offsetHeight : 0;
        if (
            !categoryBanners ||
            !categoryBanners.length ||
            (categoryBanners && categoryBanners.length && document.getElementsByClassName('no-image-categories').length)
        ) {
            //for plps desktop
            if (isHamburgerMenuBtn) {
                return bounding && bounding.top + 66 + 40 >= refinerMenuHeight;
            } else {
                return bounding && bounding.top + 66 >= refinerMenuHeight;
            }
        } else {
            //for clps desktop
            if (isHamburgerMenuBtn) {
                if (MsDyn365.isBrowser && window && window.innerWidth <= 426) {
                    return bounding && bounding.top + 190 >= refinerMenuHeight;
                }
                return bounding && bounding.top + 100 >= refinerMenuHeight;
            } else {
                return bounding && bounding.top >= refinerMenuHeight;
            }
        }
    };

    private isStickyInViewport = (): boolean => {
        let elem = document.querySelector('.ms-search-result-container__category-nav-section');
        if (!elem) {
            elem = document.querySelector('.ms-search-result-container__title');
        }
        if (!elem) {
            return false;
        }

        const rect = elem.getBoundingClientRect();

        // Visible if any part is inside viewport
        return rect.top < window.innerHeight && rect.bottom > 0;
    };

    /** Main scroll handler */
    private _mainMenuScroll = (): void => {
        const unbxdRecommendationDivs = document.getElementsByClassName('unbxd-recommendations');
        if (unbxdRecommendationDivs && unbxdRecommendationDivs.length) {
            const isVisible = this.isStickyInViewport(); // true = inside viewport
            const newScrollClassValue = !isVisible; // sticky when NOT visible

            // MOBILE LOGIC (your original behavior)
            if (MsDyn365.isBrowser && window && window.innerWidth <= 426) {
                if (this.lastChangedScrollStateMobile) {
                    const now = new Date();
                    if (now.getTime() - this.lastChangedScrollStateMobile.getTime() <= 500) {
                        return; // avoid rapid toggling
                    }
                }
            }

            // Only update state if value changes
            if (this.state.scrollClass !== newScrollClassValue) {
                if (MsDyn365.isBrowser && window && window.innerWidth <= 426) {
                    this.lastChangedScrollStateMobile = new Date();
                }

                this.setState({
                    scrollClass: newScrollClassValue
                });
            }
            return;
        }
        if (this.isInViewport()) {
            if (MsDyn365.isBrowser && window && window.innerWidth <= 426 && this.lastChangedScrollStateMobile) {
                const now = new Date();
                if (now.getTime() - this.lastChangedScrollStateMobile.getTime() <= 500) {
                    return;
                }
            }
            this.setState({
                scrollClass: false
            });
        } else if (!this.state.scrollClass) {
            if (MsDyn365.isBrowser && window && window.innerWidth <= 426) {
                this.lastChangedScrollStateMobile = new Date();
            }
            this.setState({
                scrollClass: true
            });
        }
    };
}
